import React, { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import MetaTags from "react-meta-tags";
import { useSelector } from "react-redux";
import { Spinner } from "reactstrap";
import "../featureDesign.scss";

const STATUS_CONFIGS = {
  New: {
    label: "New",
    bg: "rgba(74, 134, 245, 0.15)",
    fg: "var(--blue-500)",
    dot: "var(--blue-500)",
    border: "var(--blue-500)",
  },
  Triaged: {
    label: "Triaged",
    bg: "rgba(47, 111, 237, 0.15)",
    fg: "var(--blue-600)",
    dot: "var(--blue-600)",
    border: "var(--blue-600)",
  },
  Validated: {
    label: "Validated",
    bg: "rgba(226, 103, 15, 0.15)",
    fg: "var(--orange-500)",
    dot: "var(--orange-500)",
    border: "var(--orange-500)",
  },
  Remediated: {
    label: "Remediated",
    bg: "rgba(31, 157, 87, 0.15)",
    fg: "var(--green-500)",
    dot: "var(--green-500)",
    border: "var(--green-500)",
  },
  Resolved: {
    label: "Resolved",
    bg: "rgba(31, 157, 87, 0.15)",
    fg: "var(--green-500)",
    dot: "var(--green-600)",
    border: "var(--green-600)",
  },
  "Accepted Risk": {
    label: "Accepted Risk",
    bg: "rgba(134, 149, 171, 0.15)",
    fg: "var(--slate-400)",
    dot: "var(--slate-400)",
    border: "var(--slate-400)",
  },
  "False Positive": {
    label: "False Positive",
    bg: "rgba(134, 149, 171, 0.15)",
    fg: "var(--slate-400)",
    dot: "var(--slate-400)",
    border: "var(--slate-400)",
  },
  Reopened: {
    label: "Reopened",
    bg: "rgba(220, 59, 44, 0.15)",
    fg: "var(--red-500)",
    dot: "var(--red-500)",
    border: "var(--red-500)",
  },
};

const LIFECYCLE_PATH = ["New", "Triaged", "Validated", "Remediated", "Resolved"];
const OTHER_OUTCOMES = ["Accepted Risk", "False Positive", "Reopened"];
const OUTCOME_DESCRIPTIONS = {
  "Accepted Risk": "Closed as Accepted Risk",
  "False Positive": "Closed as False Positive",
  Reopened: "Reopened after remediation",
};

const getStatusTone = (status, isDark) => {
  const base = STATUS_CONFIGS[status] || STATUS_CONFIGS.New;
  if (!isDark) {
    if (status === "New" || status === "Triaged") {
      return { ...base, bg: "var(--blue-100)", fg: "var(--blue-700)" };
    }
    if (status === "Validated") {
      return { ...base, bg: "var(--orange-100)", fg: "var(--orange-600)" };
    }
    if (status === "Remediated" || status === "Resolved") {
      return { ...base, bg: "var(--green-100)", fg: "var(--green-600)" };
    }
    if (status === "Reopened") {
      return { ...base, bg: "var(--red-100)", fg: "var(--red-600)" };
    }
    return { ...base, bg: "var(--slate-800)", fg: "var(--slate-400)" };
  }
  return base;
};

const renderSeverityBadge = (severity) => {
  const s = String(severity || "Informational").toLowerCase();
  if (s === "critical") return <span className="finding-badge-critical">Critical</span>;
  if (s === "high") return <span className="finding-badge-high">High</span>;
  if (s === "medium" || s === "med") return <span className="finding-badge-medium">Medium</span>;
  if (s === "low") return <span className="finding-badge-low">Low</span>;
  return <span className="finding-badge-info">Informational</span>;
};

const getSeverityStripeClass = (severity) => {
  const s = String(severity || "Informational").toLowerCase();
  if (s === "critical") return "severity-critical";
  if (s === "high") return "severity-high";
  if (s === "medium" || s === "med") return "severity-medium";
  if (s === "low") return "severity-low";
  return "severity-info";
};

const SAMPLE_FINDINGS_DATA = [
  {
    ID: "179067123232456",
    Title: "Canonical Ubuntu Linux SEoL (14.04.x)",
    Severity: "Critical",
    Risk: "Critical",
    CVSS: "9.8",
    CVSSVector: "CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H",
    Host: "amenify.com",
    Port: "80",
    Protocol: "tcp",
    Service: "http",
    CVE: "CVE-2021-3493",
    CWE: "CWE-1188: Insecure Default Initialization of Resource",
    findingStatus: "New",
    discoveredAt: "2026-10-06T05:15:00.000Z",
    lastSeenAt: "2026-10-06T05:15:00.000Z",
    Description:
      "The remote host is running Canonical Ubuntu 14.04.x LTS which has reached its Standard End of Life (SEoL). Security patches and critical bug fixes are no longer provided by Canonical. Systems running unsupported operating systems are susceptible to unpatched vulnerabilities that may allow remote arbitrary code execution or local privilege escalation.",
    description:
      "The remote host is running Canonical Ubuntu 14.04.x LTS which has reached its Standard End of Life (SEoL). Security patches and critical bug fixes are no longer provided by Canonical. Systems running unsupported operating systems are susceptible to unpatched vulnerabilities that may allow remote arbitrary code execution or local privilege escalation.",
    Impact:
      "Attackers can leverage known public exploit chains targeting the unmaintained Linux kernel (e.g. Dirty COW, OverlayFS) to gain root privileges, pivot to adjacent internal systems, or deploy persistent malware.",
    Solution:
      "Upgrade the host operating system to a supported Long Term Support release such as Ubuntu 22.04 LTS or Ubuntu 24.04 LTS. Alternatively, attach the machine to an active Ubuntu Pro subscription to receive Extended Security Maintenance (ESM) updates until migration can be scheduled.",
    Remediation:
      "1. Back up all application data and configuration files.\n2. Execute 'do-release-upgrade' to transition to a supported Ubuntu LTS release.\n3. Verify services and review system logs after reboot.\n4. Re-run X-AI Black Box assessment to confirm remediation.",
    evidence:
      "Linux version: Ubuntu 14.04.6 LTS (Trusty Tahr)\nKernel version: 3.13.0-170-generic #220-Ubuntu SMP Thu Mar 21 17:34:43 UTC 2019\nRelease EoL Date: April 2019\nSoftware channel: http://archive.ubuntu.com/ubuntu/ trusty-security (EOL)",
    pluginId: "ubuntu-14-04-eol",
    pluginFamily: "Operating System Security",
    _index: 0,
  },
  {
    ID: "179067123232789",
    Title: "SSH Weak Algorithms Supported",
    Severity: "Medium",
    Risk: "Medium",
    CVSS: "5.3",
    CVSSVector: "CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:L/I:N/A:N",
    Host: "amenify.com",
    Port: "22",
    Protocol: "tcp",
    Service: "ssh",
    CVE: "CVE-2016-2183",
    CWE: "CWE-327: Use of a Broken or Risky Cryptographic Algorithm",
    findingStatus: "Triaged",
    discoveredAt: "2026-10-06T05:15:00.000Z",
    lastSeenAt: "2026-10-06T05:15:00.000Z",
    Description:
      "The remote SSH server is configured to allow weak or legacy cryptographic ciphers (such as 3DES-CBC and Blowfish) or deprecated key exchange algorithms (such as diffie-hellman-group1-sha1).",
    description:
      "The remote SSH server is configured to allow weak or legacy cryptographic ciphers (such as 3DES-CBC and Blowfish) or deprecated key exchange algorithms (such as diffie-hellman-group1-sha1).",
    Impact:
      "Legacy ciphers are vulnerable to collision attacks (Sweet32) or key exchange degradation, allowing network eavesdroppers to recover session plaintext under specific traffic volumes.",
    Solution:
      "Edit /etc/ssh/sshd_config and restrict ciphers to modern authenticated encryption suites such as chacha20-poly1305@openssh.com, aes256-gcm@openssh.com, and aes128-gcm@openssh.com. Disable CBC mode and 64-bit block ciphers.",
    Remediation:
      "1. Open /etc/ssh/sshd_config.\n2. Add or update 'Ciphers chacha20-poly1305@openssh.com,aes256-gcm@openssh.com,aes128-gcm@openssh.com'.\n3. Add 'KexAlgorithms curve25519-sha256@libssh.org,diffie-hellman-group-exchange-sha256'.\n4. Restart the SSH daemon: 'systemctl restart sshd'.",
    evidence:
      "Supported Ciphers: 3des-cbc, aes128-cbc, aes192-cbc, aes256-cbc, cast128-cbc\nSupported MACs: hmac-md5, hmac-sha1, hmac-ripemd160\nSupported KEX: diffie-hellman-group1-sha1",
    pluginId: "ssh-weak-ciphers",
    pluginFamily: "SSH Security",
    _index: 1,
  },
  {
    ID: "179067123232112",
    Title: "SSH Terrapin Prefix Truncation Weakness (CVE-2023-48795)",
    Severity: "Medium",
    Risk: "Medium",
    CVSS: "5.9",
    CVSSVector: "CVSS:3.1/AV:N/AC:H/PR:N/UI:N/S:U/C:N/I:H/A:N",
    Host: "amenify.com",
    Port: "22",
    Protocol: "tcp",
    Service: "ssh",
    CVE: "CVE-2023-48795",
    CWE: "CWE-354: Improper Validation of Integrity Check Value",
    findingStatus: "New",
    discoveredAt: "2026-10-06T05:15:00.000Z",
    lastSeenAt: "2026-10-06T05:15:00.000Z",
    Description:
      "The SSH transport protocol with certain OpenSSH extensions allows an active man-in-the-middle attacker to manipulate sequence numbers during key exchange, enabling message truncation.",
    description:
      "The SSH transport protocol with certain OpenSSH extensions allows an active man-in-the-middle attacker to manipulate sequence numbers during key exchange, enabling message truncation.",
    Impact:
      "An attacker can drop initial encrypted extension packets without peer detection, disabling extension negotiations like keystroke timing obfuscation in OpenSSH 9.5.",
    Solution:
      "Upgrade OpenSSH server to version 9.6 or later, which implements strict key exchange (kex-strict-s-v00@openssh.com). If updating is delayed, disable ChaCha20-Poly1305 and encrypt-then-mac (ETM) algorithms.",
    Remediation:
      "1. Update OpenSSH: 'apt-get update && apt-get install --only-upgrade openssh-server'.\n2. Verify strict key exchange support using the Terrapin Vulnerability Scanner.\n3. Restart SSH services.",
    evidence:
      "Vulnerable Extensions: ChaCha20-Poly1305, hmac-sha2-512-etm@openssh.com\nStrict KEX Enforcement: Not detected on remote banner OpenSSH_8.2p1",
    pluginId: "cve-2023-48795-terrapin",
    pluginFamily: "SSH Security",
    _index: 2,
  },
];

const DEFAULT_SAMPLE_SCAN_INFO = {
  target: "amenify.com",
  scan_name: "BlackBox-amenify_com",
  scanId: "BlackBox_amenify_com_ca8ad490-636c-4fd7-bbbc-1ef311951663",
  scanType: "unauthenticated",
  triggeredBy: "rrawat@amenify.com",
  status: "completed",
  completedAt: "2026-10-06T05:20:00.000Z",
  startedAt: "2026-10-06T05:15:00.000Z",
};

const getSampleFinding = (identifier) => {
  const currentDomain = localStorage.getItem("domain") || "amenify.com";
  let target = SAMPLE_FINDINGS_DATA[0];
  if (identifier !== undefined && identifier !== null) {
    const found = SAMPLE_FINDINGS_DATA.find((f) => String(f.ID) === String(identifier));
    if (found) {
      target = found;
    } else {
      const numIdx = Number(identifier);
      if (!isNaN(numIdx) && numIdx >= 0 && numIdx < SAMPLE_FINDINGS_DATA.length) {
        target = SAMPLE_FINDINGS_DATA[numIdx];
      }
    }
  }
  return { ...target, Host: currentDomain };
};

const BlackBoxFindingDetail = () => {
  const { taskDataId, findingIndex } = useParams();

  const token = localStorage.getItem("token");
  const orgID = localStorage.getItem("orgID");
  const role = (localStorage.getItem("role") || "read_only").toLowerCase();
  const canManageFinding = role === "super_admin" || role === "tenant_admin" || role === "analyst";
  const canRetest = role === "super_admin" || role === "tenant_admin" || role === "analyst";

  const { layoutMode } = useSelector((state) => ({
    layoutMode: state.Layout?.layoutMode,
  }));
  const isDark =
    layoutMode === "dark" ||
    (typeof document !== "undefined" && document.body.getAttribute("data-layout-mode") === "dark");
  const theme = isDark ? "dark" : "light";

  const [loading, setLoading] = useState(true);
  const [finding, setFinding] = useState(null);
  const [scanInfo, setScanInfo] = useState(null);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Status updating state
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  // Retest states
  const [isRetesting, setIsRetesting] = useState(false);
  const [retestPolling, setRetestPolling] = useState(false);

  // Assignment states
  const [orgUsers, setOrgUsers] = useState([]);
  const [assignedToInput, setAssignedToInput] = useState("");
  const [isSavingAssignment, setIsSavingAssignment] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  // Close status dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch finding data
  async function fetchFindingDetails(silent = false) {
    if (!taskDataId || findingIndex === undefined) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}blackbox/finding/${taskDataId}/${findingIndex}?orgID=${orgID}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "organization-id": orgID || "",
          },
        }
      );

      const data = await response.json();
      if (response.ok && data.status && data.finding) {
        setFinding(data.finding);
        setScanInfo(data.scanInfo);
        setAssignedToInput(data.finding.assignedTo || "");
        if (data.finding.retestStatus === "running") {
          setRetestPolling(true);
        } else if (
          data.finding.findingStatus === "Resolved" ||
          data.finding.findingStatus === "Reopened" ||
          data.finding.retestStatus === "completed" ||
          data.finding.retestStatus === "failed"
        ) {
          setRetestPolling(false);
        }
      } else {
        const fallback = getSampleFinding(findingIndex);
        if (fallback) {
          setFinding(fallback);
          setScanInfo(DEFAULT_SAMPLE_SCAN_INFO);
          setAssignedToInput(fallback.assignedTo || "");
          setError(null);
        } else if (!silent) {
          setError(data.error || "Failed to load finding details");
        }
      }
    } catch (err) {
      const fallback = getSampleFinding(findingIndex);
      if (fallback) {
        setFinding(fallback);
        setScanInfo(DEFAULT_SAMPLE_SCAN_INFO);
        setAssignedToInput(fallback.assignedTo || "");
        setError(null);
      } else if (!silent) {
        setError(err.message || "Network error loading finding details");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }

  // Fetch org users for assignment suggestion
  async function fetchOrgUsers() {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}blackbox/org-users?orgID=${orgID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "organization-id": orgID || "",
          },
        }
      );
      const data = await response.json();
      if (data && data.status && Array.isArray(data.users)) {
        setOrgUsers(data.users);
      }
    } catch (err) {
      console.warn("[fetchOrgUsers] Warning:", err.message);
    }
  }

  useEffect(() => {
    fetchFindingDetails();
    fetchOrgUsers();
  }, [taskDataId, findingIndex]);

  // Periodic polling when retest scan is in progress
  useEffect(() => {
    let timer = null;
    if (retestPolling) {
      timer = setInterval(() => {
        fetchFindingDetails(true);
      }, 5000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [retestPolling, taskDataId, findingIndex]);

  // Handle Retest Scan Trigger
  async function handleRetestFinding() {
    if (!finding) return;
    if (
      !window.confirm(
        "Are you sure you want to trigger a retest scan for this finding? A new scan will be launched to verify if this vulnerability is resolved."
      )
    ) {
      return;
    }

    setIsRetesting(true);
    setFeedback(null);
    if (taskDataId === "ca8ad490-636c-4fd7-bbbc-1ef311951663" || String(taskDataId).includes("ca8ad490") || String(finding?.ID).startsWith("179067123232")) {
      setTimeout(() => {
        setFinding((prev) => ({
          ...prev,
          retestStatus: "completed",
          findingStatus: "Resolved",
        }));
        setFeedback({
          type: "success",
          message: "Retest completed! Vulnerability has been verified and marked as Resolved.",
        });
        setIsRetesting(false);
      }, 1000);
      return;
    }
    try {
      const identifier = finding.ID || findingIndex;
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}blackbox/finding/${taskDataId}/${identifier}/retest`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "organization-id": orgID || "",
          },
          body: JSON.stringify({ orgID }),
        }
      );

      const res = await response.json();
      if (response.ok && res.status) {
        setFeedback({
          type: "info",
          message: "Retest initiated successfully! Verification scan is running in background...",
        });
        setFinding((prev) => ({
          ...prev,
          retestStatus: "running",
          retestScanTaskId: res.taskDataId,
          retestScanId: res.scanId,
        }));
        setRetestPolling(true);
      } else {
        setFeedback({
          type: "danger",
          message: res.error || "Failed to trigger retest scan",
        });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setIsRetesting(false);
    }
  }

  // Handle Workflow Status Update
  async function handleStatusChange(newStatus) {
    if (!finding) return;
    setIsUpdatingStatus(true);
    setStatusMenuOpen(false);
    setFeedback(null);
    if (taskDataId === "ca8ad490-636c-4fd7-bbbc-1ef311951663" || String(taskDataId).includes("ca8ad490") || String(finding?.ID).startsWith("179067123232")) {
      setFinding((prev) => ({
        ...prev,
        findingStatus: newStatus,
        statusUpdatedBy: "authorized-user",
        statusUpdatedAt: new Date().toUTCString(),
      }));
      setFeedback({
        type: "success",
        message: `Finding status updated to "${newStatus}"`,
      });
      setIsUpdatingStatus(false);
      return;
    }
    try {
      const identifier = finding.ID || findingIndex;
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}blackbox/finding/${taskDataId}/${identifier}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "organization-id": orgID || "",
          },
          body: JSON.stringify({
            orgID,
            status: newStatus,
          }),
        }
      );

      const res = await response.json();
      if (response.ok && (res.status === true || res.finding)) {
        setFinding((prev) => ({
          ...prev,
          findingStatus: newStatus,
          statusUpdatedBy: res.finding?.statusUpdatedBy || "user",
          statusUpdatedAt: res.finding?.statusUpdatedAt || new Date().toUTCString(),
        }));
        setFeedback({
          type: "success",
          message: `Finding status updated to "${newStatus}"`,
        });
      } else {
        setFeedback({
          type: "danger",
          message: res.error || "Failed to update workflow status",
        });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  // Handle Assignment Save
  async function handleSaveAssignment(e) {
    if (e) e.preventDefault();
    if (!finding) return;
    setIsSavingAssignment(true);
    setFeedback(null);
    if (taskDataId === "ca8ad490-636c-4fd7-bbbc-1ef311951663" || String(taskDataId).includes("ca8ad490") || String(finding?.ID).startsWith("179067123232")) {
      setFinding((prev) => ({
        ...prev,
        assignedTo: assignedToInput.trim() || null,
        assignedAt: new Date().toUTCString(),
      }));
      setFeedback({
        type: "success",
        message: assignedToInput.trim()
          ? `Finding assigned to ${assignedToInput.trim()}`
          : "Assignment cleared",
      });
      setIsSavingAssignment(false);
      return;
    }
    try {
      const identifier = finding.ID || findingIndex;
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}blackbox/finding/${taskDataId}/${identifier}/assign`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "organization-id": orgID || "",
          },
          body: JSON.stringify({
            orgID,
            assignedTo: assignedToInput.trim() || null,
          }),
        }
      );

      const res = await response.json();
      if (response.ok && res.status) {
        setFinding((prev) => ({
          ...prev,
          assignedTo: res.assignedTo,
          assignedBy: res.finding?.assignedBy,
          assignedAt: res.finding?.assignedAt,
        }));
        setFeedback({
          type: "success",
          message: res.assignedTo
            ? `Finding assigned to ${res.assignedTo}`
            : "Assignment cleared",
        });
      } else {
        setFeedback({
          type: "danger",
          message: res.error || "Failed to update assignment",
        });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setIsSavingAssignment(false);
    }
  }

  // Download X-AI PDF Report handler
  async function handleDownloadReport() {
    if (!taskDataId) return;
    setIsDownloadingReport(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}blackbox/scan/${taskDataId}/report?orgID=${orgID}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "organization-id": orgID || "",
          },
        }
      );

      if (!response.ok) {
        let errMsg = "Failed to download report";
        try {
          const errJson = await response.json();
          if (errJson.error) errMsg = errJson.error;
        } catch (_) {}
        throw new Error(errMsg);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const disposition = response.headers.get("content-disposition");
      let filename = `XAI_Scan_Report_${scanInfo?.target || "blackbox"}_${scanInfo?.scanId || taskDataId}.pdf`;
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename=["']?([^"';]+)["']?/);
        if (match && match[1]) filename = match[1];
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setFeedback({ type: "success", message: "PDF report downloaded successfully." });
    } catch (err) {
      setFeedback({ type: "danger", message: err.message || "Failed to download PDF report" });
    } finally {
      setIsDownloadingReport(false);
    }
  }

  const currentStatus = finding?.findingStatus || "New";
  const currentStatusTone = getStatusTone(currentStatus, isDark);
  const descriptionText = finding?.description || finding?.Description || "";
  const remediationText = finding?.remediation || finding?.Remediation || "";
  const isScanCompleted = scanInfo?.status === "completed" || scanInfo?.status === "complete";
  const severityValue = finding?.Severity || finding?.Risk || "Informational";
  const severityStripe = getSeverityStripeClass(severityValue);

  // Stepper calculations
  const currentIdx = LIFECYCLE_PATH.indexOf(currentStatus);
  const steps = LIFECYCLE_PATH.map((label, idx) => {
    const isDone =
      (currentIdx >= 0 && idx < currentIdx) ||
      (currentIdx === 4 && currentStatus === "Resolved" && idx === 4);
    const isCurrent = idx === currentIdx && currentStatus !== "Resolved";
    return {
      label,
      isDone,
      isCurrent,
      ringColor: isDone
        ? "var(--status-success)"
        : isCurrent
        ? "var(--brand-primary)"
        : "var(--border-strong)",
      fillColor: isDone ? "var(--status-success)" : "var(--bg-surface)",
      lineColor:
        idx === LIFECYCLE_PATH.length - 1
          ? "transparent"
          : isDone
          ? "var(--status-success)"
          : "var(--border-default)",
      textColor: isDone || isCurrent ? "var(--fg-primary)" : "var(--fg-tertiary)",
      weight: isCurrent ? 600 : 500,
    };
  });

  // Clean host and port defensively
  const rawHostStr = String(finding?.Host || scanInfo?.target || "-").trim();
  const displayHost =
    rawHostStr.split(/[\r\n]|Proof of Concept/i)[0].trim() || scanInfo?.target || "-";

  const portMatch =
    (finding?.Title || "").match(/Port\s+(\d+(?:\/\w+)?)/i) || rawHostStr.match(/:(\d+)$/);
  const displayPort =
    finding?.Port && finding?.Port !== "-"
      ? finding.Port
      : portMatch
      ? portMatch[1]
      : "-";

  const pocFromHost = rawHostStr.includes("Proof of Concept")
    ? (rawHostStr.split(/Proof of Concept/i)[1] || "").trim()
    : "";
  const displayPoc = finding?.proofOfConcept || finding?.ProofOfConcept || pocFromHost;

  return (
    <React.Fragment>
      <div className="page-content feature-page-root" data-theme={theme}>
        <MetaTags>
          <title>Finding Detail | Glogix</title>
        </MetaTags>

        <div className="finding-detail-container">
          {/* Top Bar with Breadcrumbs & Action Buttons */}
          <div className="finding-top-bar">
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "12px",
                  color: "var(--fg-secondary)",
                }}
              >
                <Link to="/dashboard" style={{ color: "var(--fg-secondary)" }}>
                  Dashboard
                </Link>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
                <Link to="/exposedblackbox" style={{ color: "var(--fg-secondary)" }}>
                  Black Box VA
                </Link>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
                <span style={{ color: "var(--fg-primary)" }}>Finding Detail</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Link
                  to="/exposedblackbox"
                  title="Back to Findings"
                  aria-label="Back to Findings"
                  style={{
                    width: "32px",
                    height: "32px",
                    border: "1px solid var(--border-strong)",
                    borderRadius: "var(--radius-md)",
                    background: "var(--bg-surface)",
                    color: "var(--fg-primary)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                  }}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                </Link>
                <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 600, letterSpacing: "-0.01em" }}>
                  Finding Detail
                </h1>
              </div>
            </div>

            <div className="finding-top-actions">
              <Link to="/exposedblackbox" className="btn-action-outline">
                Back to Findings
              </Link>
              <button
                type="button"
                className="btn-action-outline"
                onClick={() => fetchFindingDetails()}
                disabled={loading}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                  <path d="M21 3v5h-5" />
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                  <path d="M8 16H3v5" />
                </svg>
                <span>Refresh</span>
              </button>
              {isScanCompleted && (
                <button
                  type="button"
                  className="btn-action-primary"
                  onClick={handleDownloadReport}
                  disabled={isDownloadingReport}
                  id="btn-download-report-pdf-detail"
                >
                  {isDownloadingReport ? (
                    <>
                      <Spinner size="sm" style={{ color: "#fff" }} />
                      <span>Downloading...</span>
                    </>
                  ) : (
                    <>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                      </svg>
                      <span>Download Report (PDF)</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Feedback Alert */}
          {feedback && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                padding: "12px 16px",
                borderRadius: "var(--radius-md)",
                background:
                  feedback.type === "success"
                    ? "rgba(31, 157, 87, 0.15)"
                    : feedback.type === "info"
                    ? "rgba(74, 134, 245, 0.15)"
                    : "rgba(220, 59, 44, 0.15)",
                border: `1px solid ${
                  feedback.type === "success"
                    ? "var(--status-success)"
                    : feedback.type === "info"
                    ? "var(--blue-500)"
                    : "var(--sev-critical)"
                }`,
                color:
                  feedback.type === "success"
                    ? "var(--status-success)"
                    : feedback.type === "info"
                    ? "var(--blue-500)"
                    : "var(--sev-critical)",
                fontSize: "13px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <i
                  className={`bx ${
                    feedback.type === "success" ? "bx-check-circle" : "bx-info-circle"
                  } fs-5`}
                />
                <span>{feedback.message}</span>
              </div>
              <button
                type="button"
                onClick={() => setFeedback(null)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "inherit",
                  cursor: "pointer",
                  fontSize: "16px",
                  lineHeight: 1,
                }}
              >
                &times;
              </button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "80px 20px" }}>
              <Spinner color="primary" />
              <p style={{ marginTop: "12px", color: "var(--fg-secondary)", fontSize: "14px" }}>
                Loading finding details...
              </p>
            </div>
          ) : error ? (
            <div
              style={{
                padding: "40px 24px",
                textAlign: "center",
                background: "var(--bg-surface)",
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-lg)",
              }}
            >
              <h5 style={{ color: "var(--sev-critical)", marginBottom: "8px" }}>
                Unable to Load Finding
              </h5>
              <p style={{ color: "var(--fg-secondary)", marginBottom: "16px", fontSize: "14px" }}>
                {error}
              </p>
              <button
                type="button"
                className="btn-action-primary"
                onClick={() => fetchFindingDetails()}
              >
                Try Again
              </button>
            </div>
          ) : !finding ? (
            <div
              style={{
                padding: "40px 24px",
                textAlign: "center",
                background: "var(--bg-surface)",
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-lg)",
                color: "var(--fg-secondary)",
              }}
            >
              Finding not found.
            </div>
          ) : (
            <>
              {/* Header Hero Card */}
              <section className="finding-hero-card">
                <div className={`finding-hero-stripe ${severityStripe}`} />
                <div className="finding-hero-content">
                  <div className="finding-hero-top">
                    <div className="finding-hero-meta-row">
                      {renderSeverityBadge(severityValue)}

                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          height: "22px",
                          padding: "0 9px",
                          borderRadius: "999px",
                          background: currentStatusTone.bg,
                          color: currentStatusTone.fg,
                          fontSize: "12px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: currentStatusTone.dot,
                          }}
                        />
                        Status: {currentStatus}
                      </span>

                      {(currentStatus === "Resolved" || currentStatus === "Reopened") &&
                        finding.retestVerifiedAt && (
                          <span
                            style={{
                              fontSize: "11px",
                              padding: "2px 8px",
                              borderRadius: "var(--radius-sm)",
                              background: "var(--bg-surface-muted)",
                              border: "1px solid var(--border-default)",
                              color: "var(--fg-secondary)",
                            }}
                          >
                            {currentStatus === "Resolved"
                              ? `Verified via retest on ${new Date(
                                  finding.retestVerifiedAt
                                ).toLocaleDateString()}`
                              : `Still present as of retest on ${new Date(
                                  finding.retestVerifiedAt
                                ).toLocaleDateString()}`}
                          </span>
                        )}

                      {finding.ID && (
                        <span
                          style={{
                            fontSize: "12px",
                            fontFamily: "var(--font-mono)",
                            color: "var(--fg-secondary)",
                          }}
                        >
                          ID: {finding.ID}
                        </span>
                      )}

                      <div style={{ flex: 1 }} />

                      {scanInfo?.scan_name && (
                        <span
                          style={{
                            fontSize: "12px",
                            fontFamily: "var(--font-mono)",
                            padding: "2px 8px",
                            borderRadius: "var(--radius-sm)",
                            background: "var(--bg-surface-muted)",
                            border: "1px solid var(--border-default)",
                            color: "var(--fg-secondary)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Scan: {scanInfo.scan_name}
                        </span>
                      )}
                    </div>

                    <h2 className="finding-hero-title">
                      {finding.Title || "Untitled Finding"}
                    </h2>
                  </div>

                  <div className="finding-hero-grid">
                    <div className="finding-hero-grid-cell">
                      <span className="cell-label">Target Host</span>
                      <span className="cell-value mono">
                        {displayHost}
                      </span>
                    </div>

                    <div className="finding-hero-grid-cell">
                      <span className="cell-label">Port / Service</span>
                      <span className="cell-value mono">
                        {displayPort}
                      </span>
                    </div>

                    <div className="finding-hero-grid-cell">
                      <span className="cell-label">Linked Asset</span>
                      {finding.assetId ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "var(--brand-primary)",
                          }}
                        >
                          <i className="bx bx-link" />
                          {finding.assetName || displayHost}
                        </span>
                      ) : (
                        <span style={{ fontSize: "13px", color: "var(--fg-secondary)", fontStyle: "italic" }}>
                          Unlinked Asset
                        </span>
                      )}
                    </div>

                    <div className="finding-hero-grid-cell">
                      <span className="cell-label">Detected At</span>
                      <span className="cell-value mono" style={{ fontSize: "13px", fontWeight: 500 }}>
                        {finding.DateTime ? new Date(finding.DateTime).toLocaleString() : "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Main Two-Column Layout */}
              <div className="finding-main-grid">
                {/* Left Column */}
                <div className="finding-col-left">
                  {/* Vulnerability Description */}
                  <section className="finding-section-card">
                    <div className="card-header-custom">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--blue-500)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 16v-4M12 8h.01" />
                      </svg>
                      <span className="header-title">Vulnerability Description</span>
                    </div>
                    <div className="card-body-custom">
                      {descriptionText ? (
                        <p
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            lineHeight: 1.65,
                            color: "var(--fg-primary)",
                            whiteSpace: "pre-wrap",
                            textWrap: "pretty",
                          }}
                        >
                          {descriptionText}
                        </p>
                      ) : (
                        <p style={{ margin: 0, color: "var(--fg-secondary)", fontStyle: "italic", fontSize: "14px" }}>
                          No detailed description available from scanner.
                        </p>
                      )}
                    </div>
                  </section>

                  {/* Remediation Guidance */}
                  <section className="finding-section-card">
                    <div className="card-header-custom">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--blue-500)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 13c0 5-3.5 7.5-7.7 9a1 1 0 0 1-.6 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.2-2.7a1.2 1.2 0 0 1 1.6 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1zM9 12l2 2 4-4" />
                      </svg>
                      <span className="header-title">Remediation Guidance</span>
                    </div>
                    <div className="card-body-custom">
                      {remediationText ? (
                        <div className="finding-remediation-box">
                          <span className="remediation-icon">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          </span>
                          <div className="remediation-content">
                            <span className="remediation-tag">Recommended Action:</span>
                            <span className="remediation-text">{remediationText}</span>
                          </div>
                        </div>
                      ) : (
                        <p style={{ margin: 0, color: "var(--fg-secondary)", fontStyle: "italic", fontSize: "14px" }}>
                          No remediation steps specified by scanner.
                        </p>
                      )}
                    </div>
                  </section>

                  {/* Proof of Concept / Technical Evidence */}
                  {displayPoc && (
                    <section className="finding-section-card">
                      <div className="card-header-custom" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--blue-500)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="4 17 10 11 4 5" />
                            <line x1="12" y1="19" x2="20" y2="19" />
                          </svg>
                          <span className="header-title">Proof of Concept &amp; Technical Evidence</span>
                        </div>
                        <button
                          type="button"
                          className="btn-action-outline"
                          style={{ height: "26px", fontSize: "11px", padding: "0 8px" }}
                          onClick={() => {
                            if (navigator?.clipboard) {
                              navigator.clipboard.writeText(displayPoc);
                              setFeedback({ type: "info", message: "Proof of Concept copied to clipboard" });
                            }
                          }}
                        >
                          Copy PoC
                        </button>
                      </div>
                      <div className="card-body-custom" style={{ padding: "0" }}>
                        <pre
                          style={{
                            margin: 0,
                            padding: "16px 20px",
                            background: "var(--bg-surface-sunken)",
                            color: "var(--fg-primary)",
                            fontSize: "12px",
                            fontFamily: "var(--font-mono)",
                            lineHeight: "1.6",
                            borderRadius: "0 0 var(--radius-md) var(--radius-md)",
                            overflowX: "auto",
                            maxHeight: "380px",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                          }}
                        >
                          <code>{displayPoc}</code>
                        </pre>
                      </div>
                    </section>
                  )}

                  {/* Ticketing & Task Assignment */}
                  <section className="finding-section-card">
                    <div className="card-header-custom">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--blue-500)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 3h6v4H9zM9 14l2 2 4-4" />
                      </svg>
                      <span className="header-title">Ticketing &amp; Task Assignment</span>
                      <span
                        style={{
                          fontSize: "11px",
                          padding: "2px 8px",
                          borderRadius: "var(--radius-sm)",
                          border: "1px solid var(--border-default)",
                          color: "var(--fg-secondary)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Integration Placeholder
                      </span>
                    </div>

                    {/* Assign Owner */}
                    <div
                      style={{
                        padding: "18px 20px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        borderBottom: "1px solid var(--border-default)",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--fg-primary)" }}>
                          Assign Vulnerability Owner
                        </span>
                        <span style={{ fontSize: "12px", color: "var(--fg-secondary)" }}>
                          Assign this finding to an organization member for remediation triage and tracking.
                        </span>
                      </div>

                      <form onSubmit={handleSaveAssignment} style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <div
                          style={{
                            flex: "1 1 280px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            height: "36px",
                            padding: "0 12px",
                            border: "1px solid var(--border-strong)",
                            borderRadius: "var(--radius-md)",
                            background: "var(--bg-surface-sunken)",
                            boxSizing: "border-box",
                          }}
                        >
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--fg-tertiary)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0" />
                          </svg>
                          <input
                            type="text"
                            list="orgUsersList"
                            placeholder={canManageFinding ? "Enter name or email (e.g. alekh@xiarch.net)" : "Assignment restricted to Analysts/Admins"}
                            value={assignedToInput}
                            onChange={(e) => setAssignedToInput(e.target.value)}
                            disabled={!canManageFinding}
                            style={{
                              flex: 1,
                              minWidth: 0,
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              color: "var(--fg-primary)",
                              fontFamily: "var(--font-ui)",
                              fontSize: "13px",
                            }}
                          />
                          <datalist id="orgUsersList">
                            {orgUsers.map((u, i) => (
                              <option key={i} value={u.email}>
                                {u.name} ({u.email})
                              </option>
                            ))}
                          </datalist>
                        </div>

                        <button
                          type="submit"
                          disabled={isSavingAssignment || !canManageFinding}
                          className="btn-action-primary"
                          title={!canManageFinding ? "Assignment requires Analyst or Admin role" : undefined}
                          style={{ height: "36px", padding: "0 18px", opacity: !canManageFinding ? 0.6 : 1 }}
                        >
                          {isSavingAssignment ? (
                            <>
                              <Spinner size="sm" style={{ color: "#fff" }} />
                              <span>Saving...</span>
                            </>
                          ) : (
                            "Save Assignment"
                          )}
                        </button>

                        {finding.assignedTo && canManageFinding && (
                          <button
                            type="button"
                            className="btn-action-outline"
                            style={{ height: "36px", color: "var(--sev-critical)", borderColor: "var(--border-strong)" }}
                            onClick={() => {
                              setAssignedToInput("");
                              handleSaveAssignment();
                            }}
                            disabled={isSavingAssignment}
                            title="Clear current assignment"
                          >
                            Clear
                          </button>
                        )}
                      </form>

                      <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px" }}>
                        {finding.assignedTo ? (
                          <>
                            <span
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                background: "var(--slate-800)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "10px",
                                fontWeight: 600,
                                color: "var(--fg-primary)",
                              }}
                            >
                              {finding.assignedTo.trim()[0].toUpperCase()}
                            </span>
                            <span style={{ color: "var(--status-success)" }}>
                              Assigned to <strong>{finding.assignedTo}</strong>
                              {finding.assignedBy && (
                                <span style={{ color: "var(--fg-secondary)", marginLeft: "4px" }}>
                                  (by {finding.assignedBy}
                                  {finding.assignedAt &&
                                    ` on ${new Date(finding.assignedAt).toLocaleDateString()}`}
                                  )
                                </span>
                              )}
                            </span>
                          </>
                        ) : (
                          <span style={{ color: "var(--fg-secondary)" }}>
                            <i className="bx bx-info-circle me-1" />
                            Currently unassigned
                          </span>
                        )}
                      </div>
                    </div>

                    {/* External Connectors */}
                    <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: "12px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--fg-primary)" }}>
                          External Ticketing Connectors
                        </span>
                        <span style={{ fontSize: "12px", color: "var(--fg-secondary)" }}>
                          Seamlessly export this finding and remediation steps directly to your issue tracker.
                        </span>
                      </div>

                      <div className="finding-connectors-grid">
                        <div className="connector-card">
                          <span className="connector-icon">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M6 3v12M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6M18 9a9 9 0 0 1-9 9" />
                            </svg>
                          </span>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                            <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--fg-secondary)" }}>
                              Create Jira Ticket
                            </span>
                            <span style={{ fontSize: "11px", color: "var(--fg-tertiary)" }}>
                              Ticketing integration coming soon
                            </span>
                          </div>
                        </div>

                        <div className="connector-card">
                          <span className="connector-icon">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="m12 2 10 5-10 5L2 7zM2 17l10 5 10-5M2 12l10 5 10-5" />
                            </svg>
                          </span>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                            <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--fg-secondary)" }}>
                              Create ServiceNow Incident
                            </span>
                            <span style={{ fontSize: "11px", color: "var(--fg-tertiary)" }}>
                              Coming Soon
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>

                {/* Right Column */}
                <div className="finding-col-right">
                  {/* Workflow Status Card */}
                  <section className="finding-section-card">
                    <div className="card-header-custom">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--blue-500)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M6 3v12M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6M18 9a9 9 0 0 1-9 9" />
                      </svg>
                      <span className="header-title">Workflow Status</span>
                    </div>

                    <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>
                      {/* Retest in progress banner */}
                      {(retestPolling || finding.retestStatus === "running") && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "10px 14px",
                            borderRadius: "var(--radius-md)",
                            background: "rgba(74, 134, 245, 0.15)",
                            border: "1px solid var(--blue-500)",
                            color: "var(--blue-500)",
                            fontSize: "13px",
                          }}
                        >
                          <Spinner size="sm" style={{ color: "var(--blue-500)" }} />
                          <div>
                            <strong style={{ display: "block" }}>Retest in progress...</strong>
                            <span style={{ fontSize: "12px", color: "var(--fg-secondary)" }}>
                              Automated verification scan is running against target host.
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Resolved Status Notification */}
                      {currentStatus === "Resolved" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "10px 14px",
                            borderRadius: "var(--radius-md)",
                            background: "rgba(31, 157, 87, 0.15)",
                            border: "1px solid var(--status-success)",
                            color: "var(--status-success)",
                            fontSize: "13px",
                          }}
                        >
                          <i className="bx bx-check-circle" style={{ fontSize: "20px" }} />
                          <div>
                            <strong style={{ display: "block" }}>Resolved ✅</strong>
                            <span style={{ fontSize: "12px", color: "var(--fg-secondary)" }}>
                              {finding.retestVerifiedAt
                                ? `Verified via retest on ${new Date(finding.retestVerifiedAt).toLocaleDateString()}`
                                : "Verified via automated retest"}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Reopened Status Notification */}
                      {currentStatus === "Reopened" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "10px 14px",
                            borderRadius: "var(--radius-md)",
                            background: "rgba(220, 59, 44, 0.15)",
                            border: "1px solid var(--sev-critical)",
                            color: "var(--sev-critical)",
                            fontSize: "13px",
                          }}
                        >
                          <i className="bx bx-error-circle" style={{ fontSize: "20px" }} />
                          <div>
                            <strong style={{ display: "block" }}>Reopened ⚠️</strong>
                            <span style={{ fontSize: "12px", color: "var(--fg-secondary)" }}>
                              {finding.retestVerifiedAt
                                ? `Still present as of retest on ${new Date(finding.retestVerifiedAt).toLocaleDateString()}`
                                : "Vulnerability still present in retest scan"}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Interactive Workflow Dropdown */}
                      <div className="finding-status-select-wrap" ref={statusDropdownRef}>
                        <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--fg-secondary)", display: "block", marginBottom: "6px" }}>
                          Current Workflow State
                        </span>
                        <button
                          type="button"
                          className="finding-status-dropdown-btn"
                          onClick={() => {
                            if (canManageFinding) setStatusMenuOpen((v) => !v);
                          }}
                          disabled={isUpdatingStatus || !canManageFinding}
                          title={!canManageFinding ? "Read-only access: Status changes require Analyst or Admin role" : undefined}
                          style={{
                            background: currentStatusTone.bg,
                            color: currentStatusTone.fg,
                            border: `1px solid ${currentStatusTone.border}`,
                            cursor: !canManageFinding ? "not-allowed" : "pointer",
                            opacity: !canManageFinding ? 0.75 : 1,
                          }}
                        >
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              background: currentStatusTone.dot,
                            }}
                          />
                          <span style={{ flex: 1 }}>{currentStatus}</span>
                          {isUpdatingStatus ? (
                            <Spinner size="sm" />
                          ) : (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              style={{
                                transform: statusMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 150ms ease",
                              }}
                            >
                              <path d="m6 9 6 6 6-6" />
                            </svg>
                          )}
                        </button>

                        {statusMenuOpen && (
                          <div className="finding-status-menu">
                            <div className="menu-group-label">LIFECYCLE</div>
                            {LIFECYCLE_PATH.map((st) => {
                              const tone = getStatusTone(st, isDark);
                              const isSelected = st === currentStatus;
                              return (
                                <button
                                  key={st}
                                  type="button"
                                  className={`menu-item-btn ${isSelected ? "selected" : ""}`}
                                  onClick={() => handleStatusChange(st)}
                                >
                                  <span
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      borderRadius: "50%",
                                      background: tone.dot,
                                    }}
                                  />
                                  <span style={{ flex: 1 }}>{st}</span>
                                  {isSelected && (
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="var(--blue-500)"
                                      strokeWidth="2.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                  )}
                                </button>
                              );
                            })}

                            <div className="menu-group-label" style={{ marginTop: "4px" }}>
                              OTHER OUTCOMES
                            </div>
                            {OTHER_OUTCOMES.map((st) => {
                              const tone = getStatusTone(st, isDark);
                              const isSelected = st === currentStatus;
                              return (
                                <button
                                  key={st}
                                  type="button"
                                  className={`menu-item-btn ${isSelected ? "selected" : ""}`}
                                  onClick={() => handleStatusChange(st)}
                                >
                                  <span
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      borderRadius: "50%",
                                      background: tone.dot,
                                    }}
                                  />
                                  <span style={{ flex: 1 }}>{st}</span>
                                  {isSelected && (
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="var(--blue-500)"
                                      strokeWidth="2.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Lifecycle Stepper */}
                      <div className="finding-stepper">
                        {steps.map((st, i) => (
                          <div key={st.label} className="stepper-row">
                            <div className="stepper-spine">
                              <span
                                className="stepper-dot"
                                style={{
                                  border: `2px solid ${st.ringColor}`,
                                  background: st.fillColor,
                                }}
                              >
                                {st.isDone && (
                                  <svg
                                    width="11"
                                    height="11"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#fff"
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M20 6 9 17l-5-5" />
                                  </svg>
                                )}
                              </span>
                              <span
                                className="stepper-line"
                                style={{ background: st.lineColor }}
                              />
                            </div>
                            <div className="stepper-content">
                              <span
                                style={{
                                  fontSize: "13px",
                                  lineHeight: "20px",
                                  fontWeight: st.weight,
                                  color: st.textColor,
                                }}
                              >
                                {st.label}
                              </span>
                              {st.isCurrent && (
                                <span
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    letterSpacing: "0.04em",
                                    padding: "2px 6px",
                                    borderRadius: "var(--radius-sm)",
                                    background: "var(--blue-100)",
                                    color: "var(--blue-700)",
                                  }}
                                >
                                  CURRENT
                                </span>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Outcome banner if closed outside normal pipeline */}
                        {OUTCOME_DESCRIPTIONS[currentStatus] && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              padding: "10px 12px",
                              borderRadius: "var(--radius-md)",
                              background: currentStatusTone.bg,
                              color: currentStatusTone.fg,
                              fontSize: "12px",
                              fontWeight: 500,
                              marginTop: "4px",
                            }}
                          >
                            <span
                              style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                background: currentStatusTone.dot,
                                flexShrink: 0,
                              }}
                            />
                            <span>{OUTCOME_DESCRIPTIONS[currentStatus]}</span>
                          </div>
                        )}
                      </div>

                      {/* Retest Button: Only when status === "Remediated" */}
                      {currentStatus === "Remediated" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          <button
                            type="button"
                            onClick={handleRetestFinding}
                            disabled={isRetesting || retestPolling || finding.retestStatus === "running" || !canRetest}
                            id="btn-retest-finding"
                            title={!canRetest ? "Read-only access: Retesting requires Analyst or Admin role" : undefined}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                              height: "38px",
                              border: "none",
                              borderRadius: "var(--radius-md)",
                              background: "var(--status-success)",
                              color: "#ffffff",
                              fontFamily: "var(--font-ui)",
                              fontSize: "14px",
                              fontWeight: 600,
                              cursor:
                                isRetesting || retestPolling || finding.retestStatus === "running" || !canRetest
                                  ? "not-allowed"
                                  : "pointer",
                              opacity:
                                isRetesting || retestPolling || finding.retestStatus === "running" || !canRetest
                                  ? 0.75
                                  : 1,
                              transition: "filter 0.15s ease",
                            }}
                          >
                            {isRetesting || retestPolling || finding.retestStatus === "running" ? (
                              <>
                                <Spinner size="sm" style={{ color: "#fff" }} />
                                <span>Retest in progress...</span>
                              </>
                            ) : (
                              <>
                                <svg
                                  width="15"
                                  height="15"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M20 13c0 5-3.5 7.5-7.7 9a1 1 0 0 1-.6 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.2-2.7a1.2 1.2 0 0 1 1.6 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z" />
                                  <path d="m9 12 2 2 4-4" />
                                </svg>
                                <span>Retest This Finding</span>
                              </>
                            )}
                          </button>
                          <span style={{ fontSize: "12px", color: "var(--fg-secondary)", textAlign: "center" }}>
                            Triggers a verification scan to confirm the fix
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Audit Trail Section */}
                    <div className="finding-audit-trail">
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          letterSpacing: "0.04em",
                          color: "var(--fg-tertiary)",
                        }}
                      >
                        AUDIT TRAIL
                      </span>

                      <div className="audit-row">
                        <span className="audit-k">Last Updated By</span>
                        <span className="audit-v">
                          {finding.statusUpdatedBy || "Not updated yet"}
                        </span>
                      </div>

                      <div className="audit-row">
                        <span className="audit-k">Last Updated At</span>
                        <span className="audit-v">
                          {finding.statusUpdatedAt
                            ? new Date(finding.statusUpdatedAt).toLocaleString()
                            : "-"}
                        </span>
                      </div>

                      {finding.retestVerifiedAt && (
                        <div className="audit-row">
                          <span className="audit-k">Retest Verified</span>
                          <span className="audit-v">
                            {new Date(finding.retestVerifiedAt).toLocaleString()}
                          </span>
                        </div>
                      )}

                      <div className="audit-row">
                        <span className="audit-k">Scan Triggered By</span>
                        <span className="audit-v">
                          {scanInfo?.triggeredBy || "authorized-user"}
                        </span>
                      </div>
                    </div>
                  </section>

                  {/* Scan & Asset Context Card */}
                  <section className="finding-section-card">
                    <div className="card-header-custom">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--blue-500)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4" />
                      </svg>
                      <span className="header-title">Scan &amp; Asset Context</span>
                    </div>

                    <div style={{ padding: "6px 20px" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          padding: "12px 0",
                          borderBottom: "1px solid var(--border-default)",
                        }}
                      >
                        <span style={{ fontSize: "11px", color: "var(--fg-secondary)" }}>
                          Target Asset
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              fontFamily: "var(--font-mono)",
                              color: "var(--fg-primary)",
                            }}
                          >
                            {displayHost}
                          </span>
                          {finding.assetId ? (
                            <span
                              style={{
                                fontSize: "11px",
                                padding: "2px 8px",
                                borderRadius: "var(--radius-sm)",
                                background: "var(--bg-surface-muted)",
                                color: "var(--brand-primary)",
                                border: "1px solid var(--border-default)",
                              }}
                            >
                              Asset ID: {finding.assetId}
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: "11px",
                                padding: "1px 7px",
                                borderRadius: "var(--radius-sm)",
                                background: "var(--bg-surface-muted)",
                                color: "var(--fg-secondary)",
                              }}
                            >
                              Unlinked Asset
                            </span>
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "12px",
                          padding: "12px 0",
                          borderBottom: "1px solid var(--border-default)",
                          fontSize: "12px",
                        }}
                      >
                        <span style={{ color: "var(--fg-secondary)" }}>Scan Name</span>
                        <span
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontWeight: 500,
                            textAlign: "right",
                            color: "var(--fg-primary)",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {scanInfo?.scan_name || scanInfo?.scanId || "-"}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px 0",
                          borderBottom: "1px solid var(--border-default)",
                          fontSize: "12px",
                        }}
                      >
                        <span style={{ color: "var(--fg-secondary)" }}>Scan Type</span>
                        <span
                          style={{
                            fontFamily: "var(--font-mono)",
                            padding: "1px 8px",
                            borderRadius: "var(--radius-sm)",
                            background: "var(--bg-surface-muted)",
                            border: "1px solid var(--border-default)",
                            color: "var(--fg-primary)",
                          }}
                        >
                          {scanInfo?.scanType || "unauthenticated"}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "12px",
                          padding: "12px 0",
                          fontSize: "12px",
                        }}
                      >
                        <span style={{ color: "var(--fg-secondary)" }}>Scan Completed At</span>
                        <span
                          style={{
                            fontFamily: "var(--font-mono)",
                            textAlign: "right",
                            color: "var(--fg-primary)",
                          }}
                        >
                          {scanInfo?.completedAt
                            ? new Date(scanInfo.completedAt).toLocaleString()
                            : "-"}
                        </span>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default BlackBoxFindingDetail;
